import React, { useState } from 'react';
import { useT } from '../BuilderApp.jsx';
import LangSelect from '../../components/ui/LangSelect.jsx';
import SignInModal from '../SignInModal.jsx';
import LANGS from '../data/i18n.js';
import { walletLabel } from '../../data/packages.js';

function StepNextHint({ step }) {
  const t = useT();
  const msg = t[`next${step}`];
  if (!msg) return null;
  return (
    <div style={{display:'flex',alignItems:'center',gap:'.4rem',justifyContent:'center',marginTop:'.55rem',fontSize:'.72rem',color:'rgba(148,163,184,.55)',letterSpacing:'.01em',lineHeight:1.4}}>
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none" style={{flexShrink:0,opacity:.55}}>
        <path d="M2 5.5h7M6 2.5l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span>{msg}</span>
    </div>
  );
}

const HERO_BG = 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1800&q=80';

export default function Step1({ config, setField, next, back, lang }) {
  const t = useT();
  const [showPw, setShowPw] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const ok = config.owner_email.includes('@');

  const _t = LANGS[lang] || LANGS.en;
  const features = [
    { icon:'🟠', label: walletLabel(lang), sub: _t.feat_wallet_sub },
    { icon:'📞', label: _t.feat_phone_lbl, sub: _t.feat_phone_sub },
    { icon:'👥', label: _t.feat_crm_lbl,   sub: _t.feat_crm_sub   },
    { icon:'🌐', label: _t.feat_site_lbl,  sub: _t.feat_site_sub  },
  ];

  return (
    <>
      {showSignIn && <SignInModal onClose={() => setShowSignIn(false)} lang={lang}/>}

      {/* Sticky top bar */}
      <div style={{position:'sticky',top:0,zIndex:50,background:'rgba(5,10,20,.9)',backdropFilter:'blur(20px)',borderBottom:'1px solid var(--border)'}}>
        <div style={{display:'flex',alignItems:'center',gap:'1rem',padding:'.85rem 1.5rem'}}>
          <div style={{display:'flex',alignItems:'center',gap:'.5rem',flexShrink:0}}>
            <div style={{width:28,height:28,borderRadius:8,background:'linear-gradient(135deg,#2563eb,#7c3aed)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.85rem'}}>⚡</div>
            <span style={{fontWeight:800,fontSize:'.9rem'}}>Chamcore</span>
          </div>
          <div style={{flex:1}}>
            <div className="progress-bar"><div className="progress-fill" style={{width:'12%'}}/></div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:'.6rem',flexShrink:0}}>
            <span style={{fontSize:'.75rem',color:'var(--muted)'}}>{t.step_of(1, 8)}</span>
            {/* <LangSelect lang={lang} setLang={setLang}/> */}
          </div>
        </div>
      </div>

      {/* Hero with background */}
      <div style={{position:'relative',minHeight:'calc(100vh - 57px)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'2rem 1.25rem 3rem',overflow:'hidden'}}>
        {/* Background image */}
        <div style={{position:'absolute',inset:0,backgroundImage:`url(${HERO_BG})`,backgroundSize:'cover',backgroundPosition:'center',filter:'brightness(.22) saturate(.6)',zIndex:0}}/>
        {/* Gradient overlay */}
        <div style={{position:'absolute',inset:0,background:'linear-gradient(180deg,transparent 0%,rgba(5,10,20,.7) 100%)',zIndex:1}}/>

        {/* Content */}
        <div style={{position:'relative',zIndex:2,width:'100%',maxWidth:480}}>
          {/* Headline */}
          <div style={{textAlign:'center',marginBottom:'1.75rem'}}>
            <div style={{display:'inline-flex',alignItems:'center',gap:'.4rem',background:'rgba(37,99,235,.2)',border:'1px solid rgba(37,99,235,.3)',borderRadius:20,padding:'.3rem .8rem',fontSize:'.72rem',fontWeight:700,color:'var(--blue2)',marginBottom:'1rem',letterSpacing:'.06em'}}>
              ⚡ CHAMCORE · DIGITALISERING A–Z
            </div>
            <h1 style={{fontSize:'clamp(1.55rem,5vw,2.2rem)',fontWeight:900,letterSpacing:'-.03em',lineHeight:1.15,marginBottom:'.55rem'}}>
              {t.s1_title}
            </h1>
            <p style={{color:'rgba(148,163,184,.75)',fontSize:'.88rem',lineHeight:1.5,maxWidth:380,margin:'0 auto'}}>
              {t.s1_sub}
            </p>
          </div>

          {/* Feature strip */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(min(100%,210px),1fr))',gap:'.5rem',marginBottom:'1.5rem'}}>
            {features.map(f => (
              <div key={f.label} style={{background:'rgba(255,255,255,.055)',border:'1px solid rgba(255,255,255,.1)',borderRadius:12,padding:'.65rem .8rem',display:'flex',alignItems:'center',gap:'.6rem',backdropFilter:'blur(8px)'}}>
                <span style={{fontSize:'1.15rem',flexShrink:0}}>{f.icon}</span>
                <div>
                  <div style={{fontSize:'.75rem',fontWeight:700,color:'#e2e8f0',lineHeight:1.2}}>{f.label}</div>
                  <div style={{fontSize:'.65rem',color:'rgba(148,163,184,.7)',marginTop:'.1rem'}}>{f.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Form card */}
          <div className="glass" style={{borderRadius:20,padding:'clamp(1.5rem,4vw,2rem)'}}>
            <div style={{display:'flex',flexDirection:'column',gap:'1.1rem'}}>
              <div>
                <label className="field-label">{t.s1_email}</label>
                <input
                  type="email"
                  className="field-input"
                  placeholder="you@example.com"
                  value={config.owner_email}
                  onChange={e => setField('owner_email', e.target.value)}
                  style={{fontSize:'1rem'}}
                />
              </div>
              {/* <div>
                <label className="field-label">{t.s1_pw}</label>
                <div style={{position:'relative'}}>
                  <input
                    type={showPw ? 'text' : 'password'}
                    className="field-input"
                    placeholder={t.s1_pw_ph}
                    value={config.owner_password}
                    onChange={e => setField('owner_password', e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && ok) next(); }}
                    style={{paddingRight:'3.5rem',fontSize:'1rem'}}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(v => !v)}
                    style={{position:'absolute',right:'.8rem',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:'.78rem',padding:'.25rem'}}
                  >
                    {showPw ? t.s1_hide : t.s1_show}
                  </button>
                </div>
              </div> */}
              <button
                className="btn btn-primary btn-lg btn-full"
                onClick={next}
                disabled={!ok}
                style={{marginTop:'.15rem'}}
              >
                {t.continue}
              </button>
              <StepNextHint step={1}/>
            </div>
          </div>

          <p style={{textAlign:'center',marginTop:'1.1rem',fontSize:'.78rem',color:'rgba(100,116,139,.8)'}}>
            {t.s1_have}{' '}
            <span onClick={() => setShowSignIn(true)} style={{color:'var(--blue2)',cursor:'pointer',fontWeight:600}}>
              {t.s1_signin}
            </span>
          </p>
        </div>
      </div>
    </>
  );
}
